import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { publishSportAction, saveDraftAction } from "@/lib/actions/sports";
import { IconCheck } from "@/components/Icons";
import {
  Button,
  Card,
  Checkbox,
  Field,
  FieldRow,
  Input,
  PageHead,
  Pill,
  Problem,
  Select,
} from "@/components/kit";
import type { SportDefinitionDoc } from "@/lib/api/types";
import { assembleDoc, toCode } from "@/lib/builder";
import { displayName } from "@/lib/format";
import { sportEmoji } from "@/lib/sportIcons";

import { ConfigsSection } from "./ConfigsSection";
import { IconPicker } from "./IconPicker";
import { KindsSection } from "./KindsSection";
import { MastersSection } from "./MastersSection";

import styles from "./SportWizard.module.css";

/**
 * A sport, a step at a time. Both onboarding one and changing one.
 *
 * A wizard was tried here, removed for a one-page editor, and is back — this time
 * for both screens, which is what makes it work rather than a reversal.
 *
 * The objection to steps was real: a sport is not a sequence. A kind of fixture
 * names a master, a config names a kind, and being marched through those in
 * separate rooms means never seeing whether they agree. What answers it is that
 * the steps here are not a march. Every gate an existing sport already satisfies
 * is open, so on a published sport the rail is a set of tabs and any part is one
 * click from any other — the cross-checking the one-page editor was for, without
 * four sections' worth of form on screen at once.
 *
 * On a *blank* sport the gates do bite, and should: nobody arrives knowing what a
 * "master" is here, and reaching Publish having skipped the thing that makes
 * publishing fail is the worst way to learn. So the same screen is guided while it
 * is empty and navigable once it is real.
 *
 * One consequence worth keeping: `/sports/new` and `/{sport}/setup` are the same
 * component again. They differ in what they load, which is the only thing that was
 * ever different about them.
 */
export function SportWizard({
  initial,
  sports,
  published = false,
}: {
  initial: SportDefinitionDoc;
  /** Every other sport, for "sits inside". A sport cannot hold itself. */
  sports: { code: string; label: string }[];
  /**
   * Whether a live version already exists. Passed in rather than read off the
   * document: a definition is serialised with `exclude_unset`, so `version` is
   * absent from what the API returns and inferring from it silently made every
   * sport look new.
   */
  published?: boolean;
}) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<SportDefinitionDoc>(initial);
  const [at, setAt] = useState(0);
  const [saved, setSaved] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[] | null>(null);

  const patch = useCallback(
    (change: Partial<SportDefinitionDoc>) => setDraft((d) => ({ ...d, ...change })),
    [],
  );

  const masters = draft.masters ?? [];
  const kinds = draft.fixtureTypes ?? [];
  const configs = draft.configs ?? [];
  const grouping = draft.groups === true;

  // Debounced so typing a name is one request, not one per keystroke. A draft
  // mid-edit is usually invalid — a master with no name yet — so a failed save is
  // quiet here; publish is where it has to be right.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSaved = useRef("");
  useEffect(() => {
    if (!draft.sportCode.trim()) return;
    const payload = JSON.stringify(assembleDoc(draft));
    if (payload === lastSaved.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSaved("saving");
      const result = await saveDraftAction(assembleDoc(draft));
      if (result.ok) {
        lastSaved.current = payload;
        setSaved("saved");
      } else {
        setSaved("error");
      }
    }, 700);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [draft]);

  /**
   * The steps, derived from the draft rather than fixed.
   *
   * A grouping holds disciplines and hosts nothing, so it has no kinds of fixture
   * and no configs — and the model refuses a document that declares both. Showing
   * those two steps greyed out would be showing two steps that must stay empty;
   * they are simply not part of onboarding a grouping.
   */
  const steps = useMemo(() => {
    const list: {
      id: string;
      title: string;
      /** What this step is for, in the operator's words. */
      note: string;
      /** What is still missing, or null when the step is satisfied. */
      missing: string | null;
    }[] = [
      {
        id: "identity",
        title: "Name it",
        note: "The name becomes the code every other screen points at, so it never changes once published.",
        missing: draft.sportCode.trim() ? null : "Give the sport a name.",
      },
      {
        id: "masters",
        title: "What it keeps",
        note: "The teams, people, places and word lists this sport has. Each one becomes a screen and a row in the menu.",
        missing: masters.length === 0 ? "Add at least one thing this sport keeps." : null,
      },
    ];

    if (!grouping) {
      list.push(
        {
          id: "kinds",
          title: "Kinds of fixture",
          note: "What a contest looks like: who enters, who they field, who officiates. Swimming has two — an individual event and a relay.",
          missing: kinds.length === 0 ? "Add at least one kind of fixture." : null,
        },
        {
          id: "configs",
          title: "Rules it is played to",
          note: "The same kind of contest, different rules — T20 and Test, sevens and fifteens. Optional: skip it if there is only one way.",
          missing: null,
        },
      );
    }

    list.push({
      id: "review",
      title: "Review",
      note: published
        ? "What the next version will say. Nothing changes for anyone until you publish it."
        : "What publishing will create. Nothing is live until you press it.",
      missing: null,
    });
    return list;
  }, [draft.sportCode, masters.length, kinds.length, grouping, published]);

  // Clamp: turning a half-built sport into a grouping removes two steps from
  // under you.
  const current = Math.min(at, steps.length - 1);
  const step = steps[current];

  /** How far you may jump. Every earlier gate has to be satisfied — you can go
   *  back freely, and forward only through what you have answered. */
  const reachable = useMemo(() => {
    let i = 0;
    while (i < steps.length - 1 && steps[i].missing === null) i += 1;
    return i;
  }, [steps]);

  const blocked = steps.find((s) => s.missing !== null)?.missing ?? null;

  async function publish() {
    setBusy(true);
    setErrors(null);
    try {
      const result = await publishSportAction(assembleDoc(draft));
      if (result.ok) navigate(`/${draft.sportCode}`);
      else setErrors([result.error, ...result.violations]);
    } finally {
      // Release the button even if the action rejects at the transport layer.
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead
        title={draft.sportCode ? displayName(draft.sportCode) : "A new sport"}
        note={
          published
            ? "Saves as you go. The live version is unchanged until you publish."
            : "Saves as you go. Nothing is live until you publish."
        }
        actions={
          <div className={styles.savedState}>
            {saved === "saving" && <Pill>Saving…</Pill>}
            {saved === "saved" && (
              <Pill tone="good" dot>
                Saved
              </Pill>
            )}
            {saved === "error" && (
              <Pill tone="warn" dot>
                Not saved yet
              </Pill>
            )}
          </div>
        }
      />

      <div className={styles.layout}>
        {/* A map, not decoration: how many decisions there are, which one you are
            on, which are done. On a sport that is already published every gate is
            met, so every step is reachable and this behaves as a set of tabs. */}
        <ol className={styles.rail}>
          {steps.map((s, i) => {
            const done = i < current && s.missing === null;
            const state = i === current ? "current" : done ? "done" : "todo";
            return (
              <li key={s.id} className={styles.railItem}>
                <button
                  type="button"
                  className={`${styles.railButton} ${styles[state]}`}
                  disabled={i > reachable}
                  aria-current={i === current ? "step" : undefined}
                  onClick={() => setAt(i)}
                >
                  <span className={styles.marker} aria-hidden>
                    {done ? <IconCheck size={13} /> : i + 1}
                  </span>
                  <span className={styles.railLabel}>{s.title}</span>
                </button>
              </li>
            );
          })}
        </ol>

        <div className={styles.panel}>
          <div className={styles.stepHead}>
            <p className={styles.stepCount}>
              Step {current + 1} of {steps.length}
            </p>
            <h2 className={styles.stepTitle}>{step.title}</h2>
            <p className={styles.stepNote}>{step.note}</p>
          </div>

          <div className={styles.stepBody}>
            {step.id === "identity" && (
              <>
                <FieldRow>
                  <Field label="Name" required htmlFor="s-name">
                    <Input
                      id="s-name"
                      value={draft.sportCode}
                      placeholder="cricket"
                      autoFocus
                      onChange={(e) => patch({ sportCode: toCode(e.target.value) })}
                    />
                  </Field>
                  <Field label="Mark" hint="Shown wherever this sport appears" htmlFor="s-icon">
                    <IconPicker
                      id="s-icon"
                      value={draft.icon}
                      onChange={(icon) => patch({ icon })}
                    />
                  </Field>
                </FieldRow>

                <FieldRow>
                  <Field
                    label="Sits inside"
                    hint="Aquatics holds swimming, diving and water polo"
                    htmlFor="s-parent"
                  >
                    <Select
                      id="s-parent"
                      value={draft.partOf ?? ""}
                      onChange={(e) => patch({ partOf: e.target.value || null })}
                    >
                      <option value="">Nothing — this is a sport of its own</option>
                      {sports
                        .filter((s) => s.code !== draft.sportCode)
                        .map((s) => (
                          <option key={s.code} value={s.code}>
                            {s.label}
                          </option>
                        ))}
                    </Select>
                  </Field>
                </FieldRow>

                {/* The other half of the hierarchy, and the only way to create an
                    Aquatics through the UI at all. */}
                <div className={styles.choice}>
                  <Checkbox
                    checked={grouping}
                    label="This holds other sports rather than fixtures"
                    onChange={(e) =>
                      patch({
                        groups: e.target.checked,
                        // A grouping declares no kinds and no configs; the model
                        // refuses a document carrying both, so clear them rather
                        // than let publish fail at the last step.
                        ...(e.target.checked ? { fixtureTypes: [], configs: [] } : {}),
                      })
                    }
                  />
                  <p className={styles.choiceNote}>
                    Aquatics is one: swimming, diving and water polo sit inside it, and nothing is
                    ever entered into Aquatics itself. Choosing this drops the last two steps —
                    a grouping has no kinds of fixture.
                  </p>
                </div>
              </>
            )}

            {step.id === "masters" && (
              <MastersSection bare masters={masters} onChange={(next) => patch({ masters: next })} />
            )}

            {step.id === "kinds" && (
              <KindsSection
                bare
                kinds={kinds}
                masters={masters}
                onChange={(next) => patch({ fixtureTypes: next })}
              />
            )}

            {step.id === "configs" && (
              <ConfigsSection
                bare
                configs={configs}
                kinds={kinds}
                onChange={(next) => patch({ configs: next })}
              />
            )}

            {step.id === "review" && (
              <Review draft={draft} masters={masters} kinds={kinds} configs={configs} />
            )}
          </div>

          {errors && (
            <Problem>
              <strong>Could not publish.</strong>
              <ul className={styles.errorList}>
                {errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </Problem>
          )}

          <div className={styles.foot}>
            <Button disabled={current === 0} onClick={() => setAt(current - 1)}>
              Back
            </Button>
            <span className={styles.footGap} />
            {/* What is missing is said next to the button that is refusing, not in
                a tooltip on a disabled control nobody can hover on a phone. */}
            {step.missing && <span className={styles.missing}>{step.missing}</span>}
            {current < steps.length - 1 ? (
              <Button
                tone="primary"
                disabled={step.missing !== null}
                onClick={() => setAt(current + 1)}
              >
                Continue
              </Button>
            ) : (
              <Button tone="primary" disabled={blocked !== null || busy} onClick={publish}>
                {busy ? "Publishing…" : "Publish"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/** What publishing will create, in the shape it will create it. */
function Review({
  draft,
  masters,
  kinds,
  configs,
}: {
  draft: SportDefinitionDoc;
  masters: NonNullable<SportDefinitionDoc["masters"]>;
  kinds: NonNullable<SportDefinitionDoc["fixtureTypes"]>;
  configs: NonNullable<SportDefinitionDoc["configs"]>;
}) {
  const named = masters.filter((m) => m.code.trim() !== "");
  const levels = named.filter((m) => m.binding.kind === "competition-node");
  const parties = named.filter((m) => m.binding.kind === "participant");
  const rest = named.filter(
    (m) => m.binding.kind !== "competition-node" && m.binding.kind !== "participant",
  );

  return (
    <div className={styles.review}>
      <Card>
        <div className={styles.summary}>
          <span className={styles.summaryGlyph} aria-hidden>
            {sportEmoji(draft.icon).glyph}
          </span>
          <div>
            <p className={styles.summaryName}>{displayName(draft.sportCode)}</p>
            <p className={styles.summaryNote}>
              {draft.groups
                ? "Holds other sports"
                : draft.partOf
                  ? `Inside ${displayName(draft.partOf)}`
                  : "A sport of its own"}
            </p>
          </div>
        </div>
      </Card>

      <dl className={styles.facts}>
        <Fact label="Teams, people and places" items={parties.map((m) => m.label || m.code)} />
        <Fact label="Levels of its tree" items={levels.map((m) => m.label || m.code)} />
        <Fact label="Reference lists" items={rest.map((m) => m.label || m.code)} />
        {!draft.groups && (
          <>
            <Fact
              label="Kinds of fixture"
              items={kinds.filter((k) => k.code.trim()).map((k) => k.label || k.code)}
            />
            <Fact
              label="Rules it is played to"
              items={configs.filter((c) => c.code.trim()).map((c) => c.label || c.code)}
            />
          </>
        )}
      </dl>
    </div>
  );
}

function Fact({ label, items }: { label: string; items: string[] }) {
  return (
    <div className={styles.fact}>
      <dt className={styles.factLabel}>{label}</dt>
      <dd className={styles.factValue}>
        {items.length === 0 ? (
          <span className={styles.factNone}>none</span>
        ) : (
          items.map((item) => <Pill key={item}>{item}</Pill>)
        )}
      </dd>
    </div>
  );
}
